import { Suspense, lazy } from 'react';
import type { FC } from 'react';
import type { RouteObject } from 'react-router';
import { Navigate, Outlet } from 'react-router-dom';
import { AuthGuard } from './components/auth-guard';
import { GuestGuard } from './components/guest-guard';
import { LoadingScreen } from './components/loading-screen';
import { useAuth } from './hooks/use-auth';
import { useSubscriptionStatus } from './hooks/use-subscription-status';
import { PremiumReviewLock } from './components/subscription/premium-review-lock';
import { Account } from './containers/account/account';
import { DashboardLayout } from './containers/dashboard-layout';
import { Organization } from './containers/organization/organization';
import { Project } from './containers/project/project';

const Loadable = (Component) => (props) => (
  <Suspense fallback={<LoadingScreen />}>
    <Component {...props} />
  </Suspense>
);

// Landing path of a given IpOp review, used as the redirect target when a user
// reaches a review beyond the project's "Maximum authorized IpOp review".
const reviewLandingPath = (n: number): string => {
  switch (n) {
    case 1: return '/review-1/customers';
    case 2: return '/review-2/definition-of-success';
    case 3: return '/review-3/factors';
    default: return '/review-4/moves';
  }
};

// Redirects to the max authorized review when reviewNumber exceeds the project's
// review_max; otherwise renders the nested review routes.
// Free tier is limited to Review 1; Reviews 2–4 require a premium subscription.
const FREE_REVIEW_LIMIT = 1;

const ReviewGuard: FC<{ reviewNumber: number }> = ({ reviewNumber }) => {
  const { activeProject } = useAuth() as any;
  const { hasPremium, loading } = useSubscriptionStatus();
  const reviewMax = Number(activeProject?.review_max) || 4;

  if (reviewNumber > reviewMax) {
    return <Navigate to={reviewLandingPath(reviewMax)} replace />;
  }

  // Wait for the subscription status before deciding, so premium users don't
  // flash the lock screen.
  if (loading) {
    return <LoadingScreen />;
  }

  if (reviewNumber > FREE_REVIEW_LIMIT && !hasPremium) {
    return <PremiumReviewLock reviewNumber={reviewNumber} />;
  }

  return <Outlet />;
};

// Not found pages
// const NotFound = Loadable(lazy(() => import('./containers/not-found').then((module) => ({ default: module.NotFound }))));

// Auth pages
const Login = Loadable(lazy(
  () => import('./containers/login').then((module) => ({ default: module.Login }))
));
const PasswordRecovery = Loadable(lazy(
  () => import('./containers/password-recovery').then((module) => ({ default: module.PasswordRecovery }))
));
const PasswordReset = Loadable(lazy(
  () => import('./containers/password-reset').then((module) => ({ default: module.PasswordReset }))
));
const Register = Loadable(lazy(
  () => import('./containers/register').then((module) => ({ default: module.Register }))
));
const SignUp = Loadable(lazy(
  () => import('./containers/signup').then((module) => ({ default: module.SignUp }))
));
const VerifyEmail = Loadable(lazy(
  () => import('./containers/verify-email').then((module) => ({ default: module.VerifyEmail }))
));
const VerifyCode = Loadable(lazy(
  () => import('./containers/verify-code').then((module) => ({ default: module.VerifyCode }))
));
const AcceptInvitation = Loadable(lazy(
  () => import('./containers/accept-invitation').then((module) => ({ default: module.AcceptInvitation }))
));
const MagicLinkCallback = Loadable(lazy(
  () => import('./containers/auth/magic-link-callback').then((module) => ({ default: module.MagicLinkCallback }))
));
const ConfirmEmailChange = Loadable(lazy(
  () => import('./containers/account/confirm-email-change').then((module) => ({ default: module.ConfirmEmailChange }))
));
const Onboarding = Loadable(lazy(
  () => import('./containers/onboarding').then((module) => ({ default: module.Onboarding }))
));
const Validation = Loadable(lazy(
  () => import('./containers/validation').then((module) => ({ default: module.Validation }))
));

// Dashboard pages
const Dashboard = Loadable(lazy(
  () => import('./components/dashboard/dashboard').then((module) => ({ default: module.Dashboard }))
));

// const ReportsOverview = Loadable(lazy(
//   () => import('./containers/reports-overview').then((module) => ({ default: module.ReportsOverview }))
// ));
// const ReportsSales = Loadable(lazy(
//   () => import('./containers/reports-sales').then((module) => ({ default: module.ReportsSales }))
// ));

const AccountGeneral = Loadable(lazy(
  () => import('./containers/account/account-general').then((module) => ({ default: module.AccountGeneral }))
));
const AccountNotifications = Loadable(lazy(
  () => import('./containers/account/account-notifications').then((module) => ({ default: module.AccountNotifications }))
));
const AccountSecurity = Loadable(lazy(
  () => import('./containers/account/account-security').then((module) => ({ default: module.AccountSecurity }))
));

const Notifications = Loadable(lazy(
  () => import('./containers/notifications').then((module) => ({ default: module.Notifications }))
));

const OrganizationBilling = Loadable(lazy(
  () => import('./containers/organization/organization-billing').then((module) => ({ default: module.OrganizationBilling }))
));
const OrganizationGeneral = Loadable(lazy(
  () => import('./containers/organization/organization-general').then((module) => ({ default: module.OrganizationGeneral }))
));
const OrganizationTeam = Loadable(lazy(
  () => import('./containers/organization/organization-team').then((module) => ({ default: module.OrganizationTeam }))
));

const Company = Loadable(lazy(
  () => import('./containers/company/company').then((module) => ({ default: module.Company }))
));

// Project pages
const ProjectGeneral = Loadable(lazy(
  () => import('./containers/project/project-general').then((module) => ({ default: module.ProjectGeneral }))
));
const ProjectTeam = Loadable(lazy(
  () => import('./containers/project/project-team').then((module) => ({ default: module.ProjectTeam }))
));

const Projects = Loadable(lazy(
  () => import('./containers/project/projects').then((module) => ({ default: module.Projects }))
));
const ProjectNew = Loadable(lazy(
  () => import('./containers/project/project-new').then((module) => ({ default: module.ProjectNew }))
));

const PendingInvitations = Loadable(lazy(
  () => import('./containers/pending-invitations').then((module) => ({ default: module.PendingInvitations }))
));

// Review 1 pages
const Review1Index = Loadable(lazy(
  () => import('./containers/review-1/review-1-index').then((module) => ({ default: module.Review1Index }))
));
const Review1Customers = Loadable(lazy(
  () => import('./containers/review-1/review-1-customers').then((module) => ({ default: module.Customers }))
));
const Review1CustomerFit = Loadable(lazy(
  () => import('./containers/review-1/review-1-customer-fit').then((module) => ({ default: module.CustomerFit }))
));
const Review1Companies = Loadable(lazy(
  () => import('./containers/review-1/review-1-companies').then((module) => ({ default: module.Companies }))
));
const Review1CompanyFit = Loadable(lazy(
  () => import('./containers/review-1/review-1-company-fit').then((module) => ({ default: module.CompanyFit }))
));
const Review1Solutions = Loadable(lazy(
  () => import('./containers/review-1/review-1-solutions').then((module) => ({ default: module.Solutions }))
));

// Review 2 pages
const Review2 = Loadable(lazy(
  () => import('./containers/review-2/review-2').then((module) => ({ default: module.Review2 }))
));
const Review2Index = Loadable(lazy(
  () => import('./containers/review-2/review-2-index').then((module) => ({ default: module.Review2Index }))
));

// Review 3 pages
const Review3 = Loadable(lazy(
  () => import('./containers/review-3/review-3').then((module) => ({ default: module.Review3 }))
));
const Review3Index = Loadable(lazy(
  () => import('./containers/review-3/review-3-index').then((module) => ({ default: module.Review3Index }))
));

// Review 4 pages
const Review4 = Loadable(lazy(
  () => import('./containers/review-4/review-4').then((module) => ({ default: module.Review4 }))
));
const Review4Index = Loadable(lazy(
  () => import('./containers/review-4/review-4-index').then((module) => ({ default: module.Review4Index }))
));

// Review 4 pages
const Unknowns = Loadable(lazy(
  () => import('./containers/unknowns/unknowns').then((module) => ({ default: module.Unknowns }))
));

// Lists (one page with a tab per list)
const Lists = Loadable(lazy(
  () => import('./containers/lists/lists').then((module) => ({ default: module.Lists }))
));

// Reports
const Reports = Loadable(lazy(
  () => import('./containers/reports/reports').then((module) => ({ default: module.Reports }))
));

// Validations
const Validations = Loadable(lazy(
  () => import('./containers/validations/validations').then((module) => ({ default: module.Validations }))
));

// Assistant (RAG chat)
const Assistant = Loadable(lazy(
  () => import('./containers/assistant/assistant').then((module) => ({ default: module.Assistant }))
));

export const routes: RouteObject[] = [
  // {
  //   path: '/',
  //   element: (
  //     <MainLayout>
  //       <Home />
  //     </MainLayout>
  //   )
  // },
  {
    path: 'login',
    element: (
      <GuestGuard>
        <Login />
      </GuestGuard>
    )
  },
  {
    path: 'signup',
    element: (
      <GuestGuard>
        <SignUp />
      </GuestGuard>
    )
  },
  {
    path: 'verify-email/:token',
    element: <VerifyEmail />
  },
  {
    path: 'onboarding',
    element: (
      <AuthGuard>
        <Onboarding />
      </AuthGuard>
    )
  },
  {
    path: 'register',
    element: (
      <GuestGuard>
        <Register />
      </GuestGuard>
    )
  },
  {
    path: 'verify-code',
    element: (
      <GuestGuard>
        <VerifyCode />
      </GuestGuard>
    )
  },
  {
    path: 'password-recovery',
    element: (
      <GuestGuard>
        <PasswordRecovery />
      </GuestGuard>
    )
  },
  {
    path: 'password-reset',
    element: (
      <PasswordReset />
    )
  },
  {
    path: 'accept-invitation',
    element: (
      <GuestGuard>
        <AcceptInvitation />
      </GuestGuard>
    )
  },
  {
    // Magic-link 2FA callback — hit by users from the email we send during
    // sign-in. Public route (token in the URL is itself the secret).
    path: 'auth/magic-link',
    element: <MagicLinkCallback />
  },
  {
    // Email-change confirmation — hit from the link we mail to the NEW address.
    // Public route: it is normally opened from that mailbox, in another browser
    // (the token in the URL is itself the secret).
    path: 'account/confirm-email',
    element: <ConfirmEmailChange />
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: '/',
        element: (
          <Navigate
            to="/reports"
            replace
          />
        )
      },
      // {
      //   path: 'reports',
      //   element: <Reports />,
      //   children: [
      //     {
      //       path: '/',
      //       element: <ReportsOverview />
      //     },
      //     {
      //       path: 'sales',
      //       element: <ReportsSales />
      //     }
      //   ]
      // },
      {
        path: 'account',
        element: <Account />,
        children: [
          {
            path: '',
            element: <AccountGeneral />
          },
          {
            path: 'notifications',
            element: <AccountNotifications />
          },
          {
            path: 'security',
            element: <AccountSecurity />
          },
          {
            path: 'team',
            element: <OrganizationTeam />
          }
        ]
      },
      {
        path: 'organization',
        element: <Organization />,
        children: [
          {
            path: '',
            element: <OrganizationGeneral />
          },
          {
            path: 'team',
            element: <OrganizationTeam />
          },
          {
            path: 'billing',
            element: <OrganizationBilling />
          }
        ]
      },
      {
        path: 'company',
        element: <Company />
      },
    ]
  },
  {
    path: '/dashboard/projects/:projectId/review-1',
    element: <Review1Index />,
    children: [
      {
        path: 'customers',
        element: <Review1Customers />
      },
      {
        path: 'customers/:customerId',
        element: <Review1CustomerFit />
      },
      {
        path: 'companies',
        element: <Review1Companies />
      },
      {
        path: 'companies/:companyId',
        element: <Review1CompanyFit />
      },
      {
        path: 'solutions',
        element: <Review1Solutions />
      }
    ]
  },
  {
    path: '*',
    // element: <NotFound />
    element: (
      <GuestGuard>
        <Login />
      </GuestGuard>
    )
  }
];

export const getRoutes = (accessRights: object) => {
  const localRoutes: RouteObject[] = [
    {
      path: 'login',
      element: (
        <GuestGuard>
          <Login />
        </GuestGuard>
      )
    },
    {
      path: 'signup',
      element: (
        <GuestGuard>
          <SignUp />
        </GuestGuard>
      )
    },
    {
      path: 'verify-email/:token',
      element: <VerifyEmail />
    },
    {
      path: 'onboarding',
      element: (
        <AuthGuard>
          <Onboarding />
        </AuthGuard>
      )
    },
    {
      path: 'register/:id',
      element: (
        <GuestGuard>
          <Register />
        </GuestGuard>
      )
    },
    {
      path: 'verify-code',
      element: (
        <GuestGuard>
          <VerifyCode />
        </GuestGuard>
      )
    },
    {
      path: 'password-recovery',
      element: (
        <GuestGuard>
          <PasswordRecovery />
        </GuestGuard>
      )
    },
    {
      path: 'password-reset',
      element: (
        <GuestGuard>
          <PasswordReset />
        </GuestGuard>
      )
    },
    {
      path: 'accept-invitation/:id',
      element: (
        <GuestGuard>
          <AcceptInvitation />
        </GuestGuard>
      )
    },
    {
      path: 'validation/:id',
      element: (
        <GuestGuard>
          <Validation />
        </GuestGuard>
      )
    },
    {
      path: '*',
      // element: <NotFound />
      element: (
        <GuestGuard>
          <Login />
        </GuestGuard>
      )
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: '/',
          element: (
            <Dashboard />
          )
        },
        {
          path: 'notifications',
          element: <Notifications />
        },
        {
          path: 'pending-invitations',
          element: <PendingInvitations />
        },
        // {
        //   path: 'reports',
        //   element: <Reports />,
        //   children: [
        //     {
        //       path: '/',
        //       element: <ReportsOverview />
        //     },
        //     {
        //       path: 'sales',
        //       element: <ReportsSales />
        //     }
        //   ]
        // },
        {
          path: 'account',
          element: <Account />,
          children: [
            {
              path: '',
              element: <AccountGeneral />
            },
            {
              path: 'notifications',
              element: <AccountNotifications />
            },
            {
              path: 'security',
              element: <AccountSecurity />
            },
            {
              path: 'team',
              element: <OrganizationTeam />
            }
          ]
        },
        {
          path: 'company',
          element: <Company />
        },
        {
          path: 'assistant',
          element: <Assistant />
        },
        // {
        //   path: 'organization',
        //   element: <Organization />,
        //   children: [
        //     {
        //       path: '/',
        //       element: <OrganizationGeneral />
        //     },
        //     {
        //       path: '/team',
        //       element: <OrganizationTeam />
        //     },
        //     {
        //       path: '/billing',
        //       element: <OrganizationBilling />
        //     }
        //   ]
        // },
      ]
    }
  ];

  if (accessRights) {
    if (accessRights['project_admin_space'] === 1) {
      localRoutes[localRoutes.length - 1]['children'].push(
        {
          path: 'organization',
          element: <Organization />,
          children: [
            {
              path: '',
              element: <OrganizationGeneral />
            },
            {
              path: 'team',
              element: <OrganizationTeam />
            },
            {
              path: 'billing',
              element: <OrganizationBilling />
            }
          ]
        },
        {
          path: 'project',
          element: <Project />,
          children: [
            {
              path: '',
              element: <ProjectGeneral />
            },
            {
              path: 'team',
              element: <ProjectTeam />
            },
            // {
            //   path: '/billing',
            //   element: <OrganizationBilling />
            // }
          ]
        },

      );
    }

    if (accessRights['review_1'] === 1 || accessRights['review_1'] === 2) {
      localRoutes[localRoutes.length - 1]['children'].push(
        {
          path: 'review-1',
          element: <ReviewGuard reviewNumber={1} />,
          children: [
            {
              path: '',
              element: <Review1Index />
            },
            {
              path: 'customers',
              element: <Review1Customers />
            },
            {
              path: 'companies',
              element: <Review1Companies />
            },
            {
              path: 'customer-fit/:id',
              element: <Review1CustomerFit />
            },
            {
              path: 'company-fit/:id',
              element: <Review1CompanyFit />
            },
            {
              path: 'solutions',
              element: <Review1Solutions />
            }
          ]
        }
      )
    }

    if (accessRights['review_2'] === 1 || accessRights['review_2'] === 2) {
      localRoutes[localRoutes.length - 1]['children'].push(
        {
          path: 'review-2',
          element: <ReviewGuard reviewNumber={2} />,
          children: [
            {
              path: '',
              element: <Review2 />
            },
            {
              path: 'definition-of-success',
              element: <Review2 />
            },
            {
              path: 'definition-of-failure',
              element: <Review2 />
            },
            {
              path: 'activity-chain',
              element: <Review2 />
            },
            {
              path: 'economic-model',
              element: <Review2 />
            },
            {
              path: 'benefit',
              element: <Review2 />
            },
            {
              path: 'skills',
              element: <Review2 />
            },
          ]
        }
      )
    }

    if (accessRights['review_3'] === 1 || accessRights['review_3'] === 2) {
      localRoutes[localRoutes.length - 1]['children'].push(
        {
          path: 'review-3',
          element: <ReviewGuard reviewNumber={3} />,
          children: [
            {
              path: '',
              element: <Review3 />
            },
            {
              path: 'factors',
              element: <Review3 />
            },
            {
              path: 'stakeholders',
              element: <Review3 />
            },
          ]
        }
      )
    }

    if (accessRights['review_4'] === 1 || accessRights['review_4'] === 2) {
      localRoutes[localRoutes.length - 1]['children'].push(
        {
          path: 'review-4',
          element: <ReviewGuard reviewNumber={4} />,
          children: [
            {
              path: '',
              element: <Review4 />
            },
            {
              path: 'moves',
              element: <Review4 />
            },
            {
              path: 'moves2',
              element: <Review4 />
            },
            {
              path: 'intellectual-properties',
              element: <Review4 />
            },
            {
              path: 'timeline',
              element: <Review4 />
            },
            {
              path: 'budget-summary',
              element: <Review4 />
            },
          ]
        }
      )
    }

    if (accessRights['review_4'] === 1 || accessRights['review_4'] === 2) {
      localRoutes[localRoutes.length - 1]['children'].push(
        {
          path: 'unknowns',
          element: <Unknowns />
          // children: [
          //   {
          //     path: '/economic-model',
          //     element: <Review2EconomicModel />
          //   },
          // ]
        }
      )
    }

    if (accessRights['review_4'] === 1 || accessRights['review_4'] === 2) {
      localRoutes[localRoutes.length - 1]['children'].push(
        {
          path: 'lists',
          element: <Lists />
        }
      )
    }

    if (accessRights['review_4'] === 1 || accessRights['review_4'] === 2) {
      localRoutes[localRoutes.length - 1]['children'].push(
        {
          path: 'reports',
          element: <Reports />
          // children: [
          //   {
          //     path: '/economic-model',
          //     element: <Review2EconomicModel />
          //   },
          // ]
        }
      )
    }

    if (accessRights['review_4'] === 1 || accessRights['review_4'] === 2) {
      localRoutes[localRoutes.length - 1]['children'].push(
        {
          path: 'validations',
          element: <Validations />
          // children: [
          //   {
          //     path: '/economic-model',
          //     element: <Review2EconomicModel />
          //   },
          // ]
        }
      )
    }

    localRoutes[localRoutes.length - 1]['children'].push(
      {
        path: 'projects',
        // element: <Organization />,
        children: [
          {
            path: '',
            element: <Projects />
          },
          {
            path: 'new',
            element: <ProjectNew />
          },
          // {
          //   path: '/billing',
          //   element: <OrganizationBilling />
          // }
        ]
      },
    );
  }

  return localRoutes;
};
